import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Users } from "lucide-react";
import { cn } from "@/app/_utils/cn";
import { dayjsTz } from "@/app/_utils/dayjs";
import GuestService from "@/app/_services/GuestService";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";

const formatter = new Intl.NumberFormat("en-US");

function pctChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

export default function WaitlistSizeStat() {
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState<number | null>(null);
  const [trendPct, setTrendPct] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    GuestService.getGuests({ since: dayjsTz().subtract(7, "days").format("YYYY-MM-DD") })
      .then((res) => {
        if (cancelled) return;
        const totalNow = Number(res?.guest_count ?? 0);
        const thisWeekSignups = Array.isArray(res?.data) ? res.data.length : 0;
        const totalAWeekAgo = totalNow - thisWeekSignups;
        setTotal(totalNow);
        setTrendPct(pctChange(totalNow, totalAWeekAgo));
      })
      .catch(() => {
        if (!cancelled) setTotal(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-end gap-1">
        <Skeleton className="h-[18px] w-16" />
        <Skeleton className="hidden h-3 w-20 sm:block" />
      </div>
    );
  }

  if (total === null) return null;

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center gap-1.5 text-[16px] font-bold leading-none tracking-tight text-foreground sm:text-[18px]">
        <Users className="size-3.5 text-muted-foreground/60" />
        {formatter.format(total)}
      </div>
      {trendPct !== null && (
        <div className="mt-1 hidden items-center gap-1 text-[10px] sm:flex">
          <span
            className={cn(
              "flex items-center gap-0.5 font-medium",
              trendPct >= 0 ? "text-green-600" : "text-red-600"
            )}
          >
            {trendPct >= 0 ? (
              <TrendingUp className="size-3" />
            ) : (
              <TrendingDown className="size-3" />
            )}
            {trendPct >= 0 ? "+" : ""}
            {trendPct.toFixed(1)}%
          </span>
          <span className="text-muted-foreground">from last week</span>
        </div>
      )}
    </div>
  );
}
